import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";

const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const AVATAR = "https://customer-assets-jai6qajn.emergentagent.net/job_premium-coach-16/artifacts/2t2go6bj_629899427_1327380086097439_2033080322372163309_n.jpg";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genId);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ciao! Sono l'assistente di Mirko. Come posso aiutarti oggi? Posso raccontarti i percorsi di coaching o aiutarti a prenotare un primo colloquio gratuito." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Mi dispiace, si è verificato un problema. Riprova tra poco." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#C19A5B] text-black flex items-center justify-center shadow-lg shadow-black/40 hover:bg-[#D4AF37] transition-colors"
        aria-label="Chat"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div data-testid="chat-window" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[540px] bg-[#1A1A1A] border border-white/10 rounded-2xl flex flex-col overflow-hidden shadow-2xl shadow-black/60">
          <div className="px-5 py-4 border-b border-white/10 bg-[#121212] flex items-center gap-3">
            <img src={AVATAR} alt="Mirko" className="h-10 w-10 rounded-full object-cover border border-[#C19A5B]/50" />
            <div>
              <p className="font-display text-lg text-white leading-tight">Assistente di Mirko</p>
              <p className="text-xs text-[#C19A5B]">Sempre attivo · risponde in italiano</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && <img src={AVATAR} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />}
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#C19A5B] text-black" : "bg-[#242424] text-zinc-100"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Scrivi un messaggio…"
              className="flex-1 bg-[#121212] border border-[#27272A] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#C19A5B]"
            />
            <button data-testid="chat-send" onClick={send} disabled={busy} className="h-10 w-10 rounded-full bg-[#C19A5B] text-black flex items-center justify-center hover:bg-[#D4AF37] transition-colors disabled:opacity-50">
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
